import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, BedDouble, Bath, Maximize, MapPin, Compass } from "lucide-react";
import { useState } from "react";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { formatPrice, typeLabel, img } from "../lib/utils-app";
import { toast } from "sonner";

export function PropertyCard({ property, index = 0 }) {
  const { L, t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [fav, setFav] = useState(false);
  const hasImages = (property.images || []).length > 0;
  const isRent = property.transaction_type === "rent";
  const img0 = (property.images && property.images[0]) || "";

  const toggleFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error(lang === "es" ? "Inicia sesión para guardar" : "Sign in to save homes");
      return;
    }
    try {
      if (fav) {
        await api.delete(`/favorites/${property.id}`);
        setFav(false);
      } else {
        await api.post(`/favorites/${property.id}`);
        setFav(true);
        toast.success(t("common.saved"));
      }
    } catch {
      toast.error("Error");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: (index % 6) * 0.06 }}
      data-testid={`property-card-${property.id}`}
    >
      <Link to={`/property/${property.id}`} className="card-lift group relative block bg-card border border-primary/10 hover:border-primary/30">
        <div
          className="pointer-events-none absolute -top-3 right-6 z-30"
          style={{ filter: "drop-shadow(0 8px 16px rgba(0,0,0,0.35))" }}
          data-testid={`consultation-ribbon-${property.id}`}
        >
          <div className="relative transition-transform duration-500 group-hover:-translate-y-0.5">
            <div
              className="absolute inset-0 bg-[#f0cf88]"
              style={{ clipPath: "polygon(13px 0, calc(100% - 13px) 0, 100% 50%, calc(100% - 13px) 100%, 13px 100%, 0 50%)" }}
            />
            <div
              className="relative m-[1.5px] bg-gradient-to-b from-[#d42a2a] via-[#b81c1c] to-[#8f1111] text-[#f8e1a0] uppercase font-semibold tracking-[0.16em] text-[0.6rem] px-6 py-1.5"
              style={{ clipPath: "polygon(13px 0, calc(100% - 13px) 0, 100% 50%, calc(100% - 13px) 100%, 13px 100%, 0 50%)" }}
            >
              {lang === "es" ? "Consulta Gratis" : "Free Consultation"}
            </div>
          </div>
        </div>
        <div className="relative aspect-[4/3] hover-zoom overflow-hidden">
          <img src={img(img0)} alt={L(property.title)} className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 scrim opacity-60" />
          {property.featured && (
            <div className="pointer-events-none absolute top-[18px] -left-[42px] w-[170px] rotate-[-45deg] bg-gradient-to-r from-[#a9781f] via-[#f0cf88] to-[#a9781f] text-[#241703] text-center label-caps text-[0.58rem] py-1 shadow-[0_4px_12px_rgba(0,0,0,0.35)] z-20" data-testid={`featured-ribbon-${property.id}`}>
              {t("common.featured")}
            </div>
          )}
          <div className={`absolute top-3 flex gap-2 z-10 ${property.featured ? "right-14" : "left-3"}`}>
            <span className={`label-caps px-2.5 py-1 ${isRent ? "bg-accent text-accent-foreground" : "bg-primary text-primary-foreground"}`}>
              {isRent ? t("common.forRent") : t("common.forSale")}
            </span>
          </div>
          <button
            onClick={toggleFav}
            data-testid={`favorite-btn-${property.id}`}
            className="absolute top-3 right-3 w-9 h-9 grid place-items-center bg-black/40 backdrop-blur border border-white/20 hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
            aria-label="Save"
          >
            <Heart size={16} className={fav ? "fill-primary text-primary" : ""} />
          </button>
          <div className="absolute bottom-3 left-3 flex items-center gap-1.5 text-white/90 text-sm">
            <MapPin size={13} className="text-primary" />
            <span className="font-light">{property.neighborhood}, {property.city}</span>
          </div>
          {hasImages && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/virtual-tour/${property.id}`); }}
              data-testid={`virtual-tour-btn-${property.id}`}
              className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-black/45 backdrop-blur border border-white/25 text-white text-[0.62rem] label-caps px-2.5 py-1.5 hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
            >
              <Compass size={13} /> {lang === "es" ? "Tour Virtual" : "Virtual Tour"}
            </button>
          )}
        </div>
        <div className="p-5">
          <div className="flex items-baseline justify-between gap-3 mb-1.5">
            <h3 className="font-serif text-xl leading-tight group-hover:text-primary transition-colors duration-300 truncate">
              {L(property.title)}
            </h3>
          </div>
          <p className="font-serif text-2xl text-primary mb-3">
            {formatPrice(property.price, property.currency)}
            {isRent && <span className="text-sm text-muted-foreground font-sans">{t("common.perMonth")}</span>}
          </p>
          <p className="label-caps text-muted-foreground mb-3">{typeLabel(property.property_type, lang)}</p>
          <div className="flex items-center gap-4 text-sm text-muted-foreground border-t border-border pt-3">
            {property.bedrooms > 0 && (
              <span className="flex items-center gap-1.5"><BedDouble size={15} className="text-primary/70" /> {property.bedrooms}</span>
            )}
            {property.bathrooms > 0 && (
              <span className="flex items-center gap-1.5"><Bath size={15} className="text-primary/70" /> {property.bathrooms}</span>
            )}
            {property.sqft > 0 && (
              <span className="flex items-center gap-1.5"><Maximize size={15} className="text-primary/70" /> {property.sqft.toLocaleString()}</span>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
